import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  bomItemsAdminApi,
  getBomItemTypes,
  getBomCalcTypes,
  type BomItemRow,
  type CreateBomItemBody,
  type UpdateBomItemBody,
} from "@/lib/bom-items";
import { productsAdminApi } from "@/lib/products-admin";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { RangePricingEditor, type RangePricingValue } from "@/components/bom/RangePricingEditor";
import { parseCostByRange, serializeCostByRange, rangeRowErrors } from "@/lib/cost-by-range";
import { toast } from "@/hooks/use-toast";

// Flat RHF fields. Range pricing (priceByRange + rows) is handled by
// RangePricingEditor in page state and folded into the body on save.
const itemSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  bomItemTypeCode: z.string().min(1),
  calcType: z.string().min(1),
  calcFactor: z.number().nullable(),
  unitCost: z.number().nullable(),
  unitCurrencyCode: z.string().nullable(),
  supplierId: z.number().int().nullable(),
  itemiseOnQuote: z.boolean(),
  note: z.string().nullable(),
});

type ItemForm = z.infer<typeof itemSchema>;

const BLANK_FORM: ItemForm = {
  name: "",
  bomItemTypeCode: "",
  calcType: "",
  calcFactor: null,
  unitCost: null,
  unitCurrencyCode: "AUD",
  supplierId: null,
  itemiseOnQuote: true,
  note: null,
};

const ADMIN_LIST_KEY = ["bom-items", "admin"] as const;

// `setValueAs` helpers for register() on the numeric inputs.
const asNumberOrNull = (v: string) => (v === "" ? null : Number(v));

export function MasterItemsAdminPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BomItemRow | null>(null);

  const [range, setRange] = useState<RangePricingValue>({ priceByRange: false, rows: [] });

  // Seed the range editor whenever the drawer target changes.
  useEffect(() => {
    if (editing) {
      setRange({
        priceByRange: editing.priceByRange,
        rows: parseCostByRange(editing.unitCostByRangeJson),
      });
    } else if (creating) {
      setRange({ priceByRange: false, rows: [] });
    }
  }, [editing, creating]);

  const rangeHasErrors =
    range.priceByRange &&
    (range.rows.length === 0 || range.rows.some((r) => rangeRowErrors(r).length > 0));

  // List (0-based paging — first page page=0)
  const { data, isLoading } = useQuery({
    queryKey: [...ADMIN_LIST_KEY, page, q],
    queryFn: () => bomItemsAdminApi.listAdmin({ q, page }),
  });

  // Lookups — type + calc dropdowns
  const { data: itemTypes } = useQuery({
    queryKey: ["bom-item-types"],
    queryFn: () => getBomItemTypes(),
  });
  const { data: calcTypes } = useQuery({
    queryKey: ["bom-calc-types"],
    queryFn: () => getBomCalcTypes(),
  });

  // Supplier list (reuse the existing catalog dropdown helper)
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => productsAdminApi.suppliers(),
  });

  // Create / update mutation
  const upsert = useMutation({
    mutationFn: (form: ItemForm) => {
      const note = form.note && form.note.trim() !== "" ? form.note : null;
      // The native "— None —" supplier option resolves to a falsy value (0/null),
      // never a real supplier id (which start at 1). Normalise it to null so the
      // clear flag fires and we never send supplierId:0 to the server.
      const supplierId = form.supplierId ? form.supplierId : null;
      if (editing) {
        const body: UpdateBomItemBody = {
          name: form.name,
          bomItemTypeCode: form.bomItemTypeCode,
          calcType: form.calcType,
          calcFactor: form.calcFactor ?? null,
          unitCost: range.priceByRange ? null : (form.unitCost ?? null),
          unitCurrencyCode: form.unitCurrencyCode ?? null,
          priceByRange: range.priceByRange,
          unitCostByRangeJson: range.priceByRange ? serializeCostByRange(range.rows) : null,
          supplierId,
          itemiseOnQuote: form.itemiseOnQuote,
          note,
          clearSupplierId: supplierId == null,
          clearNote: note == null,
        };
        return bomItemsAdminApi.update(editing.bomItemId, body, editing.rowVersion);
      }
      // Create stays flat (CreateBomItemBody has no range/clear fields; server
      // defaults priceByRange=false). Range pricing is captured on the next edit.
      const body: CreateBomItemBody = {
        name: form.name,
        bomItemTypeCode: form.bomItemTypeCode,
        calcType: form.calcType,
        calcFactor: form.calcFactor ?? null,
        unitCost: form.unitCost ?? null,
        unitCurrencyCode: form.unitCurrencyCode ?? null,
        supplierId,
        itemiseOnQuote: form.itemiseOnQuote,
        note,
      };
      return bomItemsAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      setCreating(false);
      setEditing(null);
    },
    onError: (err: Error) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  // Delete mutation
  const remove = useMutation({
    mutationFn: (row: BomItemRow) => bomItemsAdminApi.remove(row.bomItemId, row.rowVersion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
    },
    onError: (err: Error) => {
      toast.error(`Cannot delete: ${err.message}`);
    },
  });

  // Resolve lookup labels for display
  const typeMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of itemTypes ?? []) m.set(t.bomItemTypeCode, t.name);
    return m;
  }, [itemTypes]);
  const calcMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calcTypes ?? []) m.set(c.calcType, c.calcName);
    return m;
  }, [calcTypes]);
  const supplierMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of suppliers ?? []) m.set(s.id, s.name);
    return m;
  }, [suppliers]);

  // Columns
  const cols: DataGridColumn<BomItemRow>[] = [
    { id: "name", header: "Name", sortable: true, accessor: (r) => r.name },
    {
      id: "type",
      header: "Type",
      accessor: (r) => typeMap.get(r.bomItemTypeCode) ?? r.bomItemTypeCode,
    },
    {
      id: "calc",
      header: "Calc",
      accessor: (r) => calcMap.get(r.calcType) ?? r.calcType,
    },
    {
      id: "unitCost",
      header: "Unit cost",
      accessor: (r) =>
        r.priceByRange
          ? "By range"
          : r.unitCost != null
            ? `${r.unitCurrencyCode} ${r.unitCost.toFixed(2)}`
            : "—",
    },
    {
      id: "supplier",
      header: "Supplier",
      accessor: (r) =>
        r.supplierId != null ? (supplierMap.get(r.supplierId) ?? String(r.supplierId)) : "—",
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex gap-1">
          <IntentButton intent="ghost" onClick={() => setEditing(r)}>
            Edit
          </IntentButton>
          <IntentButton intent="ghost" onClick={() => remove.mutate(r)}>
            Delete
          </IntentButton>
        </div>
      ),
    },
  ];

  // Default form values when editing — flat fields only.
  function toFormValues(row: BomItemRow): ItemForm {
    return {
      name: row.name,
      bomItemTypeCode: row.bomItemTypeCode,
      calcType: row.calcType,
      calcFactor: row.calcFactor,
      unitCost: row.unitCost,
      unitCurrencyCode: row.unitCurrencyCode,
      supplierId: row.supplierId,
      itemiseOnQuote: row.itemiseOnQuote,
      note: row.note,
    };
  }

  const drawerOpen = creating || editing !== null;
  const drawerTitle = editing ? "Edit master item" : "New master item";

  return (
    <ListPage backTo="/admin"
      title="Master items"
      primaryAction={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Master Item</Button>}
      search={{
        value: q,
        onChange: (v) => {
          setQ(v);
          setPage(0);
        },
        placeholder: "Search name…",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.bomItemId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={drawerTitle}
      >
        <EntityForm
          schema={itemSchema}
          defaultValues={editing ? toFormValues(editing) : BLANK_FORM}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              {/* Name */}
              <div className="space-y-1">
                <Label htmlFor="bi-name">Name</Label>
                <Input id="bi-name" {...form.register("name")} />
              </div>

              {/* Type dropdown */}
              <div className="space-y-1">
                <Label htmlFor="bi-type">Type</Label>
                <select
                  id="bi-type"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("bomItemTypeCode")}
                >
                  <option value="">— Select —</option>
                  {(itemTypes ?? []).map((t) => (
                    <option key={t.bomItemTypeCode} value={t.bomItemTypeCode}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Calc dropdown */}
              <div className="space-y-1">
                <Label htmlFor="bi-calc">Calc</Label>
                <select
                  id="bi-calc"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("calcType")}
                >
                  <option value="">— Select —</option>
                  {(calcTypes ?? []).map((c) => (
                    <option key={c.calcType} value={c.calcType}>
                      {c.calcName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Calc factor */}
              <div className="space-y-1">
                <Label htmlFor="bi-factor">Calc factor</Label>
                <Input
                  id="bi-factor"
                  type="number"
                  step="any"
                  {...form.register("calcFactor", { setValueAs: asNumberOrNull })}
                />
              </div>

              {/* Unit cost (money) — disabled when range pricing is on */}
              <div className="space-y-1">
                <Label htmlFor="bi-unitcost">Unit cost</Label>
                <Input
                  id="bi-unitcost"
                  type="number"
                  step="0.01"
                  disabled={range.priceByRange}
                  {...form.register("unitCost", { setValueAs: asNumberOrNull })}
                />
              </div>

              {/* Range pricing toggle + table (flat Unit cost above is disabled when on) */}
              <RangePricingEditor value={range} onChange={setRange} />

              {/* Currency */}
              <div className="space-y-1">
                <Label htmlFor="bi-currency">Currency</Label>
                <Input id="bi-currency" {...form.register("unitCurrencyCode")} />
              </div>

              {/* Supplier dropdown */}
              <div className="space-y-1">
                <Label htmlFor="bi-supplier">Supplier</Label>
                <select
                  id="bi-supplier"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("supplierId", { setValueAs: asNumberOrNull })}
                >
                  <option value="">— None —</option>
                  {(suppliers ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Itemise on quote */}
              <div className="flex items-center gap-2">
                <input id="bi-itemise" type="checkbox" {...form.register("itemiseOnQuote")} />
                <Label htmlFor="bi-itemise">Itemise on quote</Label>
              </div>

              {/* Note */}
              <div className="space-y-1">
                <Label htmlFor="bi-note">Note</Label>
                <Input id="bi-note" {...form.register("note")} />
              </div>

              <Button type="submit" disabled={rangeHasErrors}>Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
